import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import { ApolloError } from '@apollo/client';

const category = 'admin.contactcollector';

type ButtonType = 'edit' | 'save' | 'navigation' | 'share';

/**
 * Admin-side contact collector elements that can be interacted with.
 * Each element represents a specific UI interaction point for telemetry tracking.
 */
type CCAdminElement = 'copyLink' | 'modifySettings' | 'previewAsGuest' | 'editGuestList' | 'createMagicLink' | 'updateMagicLink' | 'browseCardDesigns' | 'printCrossSellSmartTip';

/** Extra info for magic link creation/update tracking */
type MagicLinkExtraInfo = Readonly<{
  eventHandle: string;
  visual: string;
  animation: string;
  message: string;
  linkToWebsite: boolean;
  displayDate: boolean;
  eventDate: string;
}>;

/** Extra info for print cross-sell tracking */
type PrintCrossSellExtraInfo = Readonly<{
  step: string;
}>;

/**
 * Discriminated union for buttonInteracted params.
 * Each element that requires extraInfo has its own variant with typed payload.
 */
type CCButtonInteractedParams =
  | Readonly<{
      buttonType: ButtonType;
      element: 'createMagicLink' | 'updateMagicLink';
      extraInfo: MagicLinkExtraInfo;
    }>
  | Readonly<{
      buttonType: ButtonType;
      element: 'printCrossSellSmartTip';
      extraInfo: PrintCrossSellExtraInfo;
    }>
  | Readonly<{
      buttonType: ButtonType;
      element: Exclude<CCAdminElement, 'createMagicLink' | 'updateMagicLink' | 'printCrossSellSmartTip'>;
      extraInfo?: undefined;
    }>;

const contactCollectorTelemetry = createTelemetryObject({
  pages: {
    printCrossSellModal: () => ({
      category: 'contactCollector',
      page: 'printCrossSellModal',
      pagePrefix: 'admin',
    }),
    pageViewed: (pageName: string, extraInfo: Record<string, unknown> = {}) => ({
      category: 'contactCollector',
      page: pageName,
      pagePrefix: 'admin',
      extraInfo,
    }),
  },
  actions: {
    trackError: (action: 'Query' | 'Mutation' | 'Other', error: ApolloError | Error | unknown) => ({
      category,
      action,
      actionType: 'error',
      extraInfo: { error },
    }),

    buttonInteracted: ({ buttonType, element, extraInfo }: CCButtonInteractedParams) => ({
      category,
      action: 'ButtonInteracted',
      extraInfo: {
        name: `contactCollectorAdmin_${buttonType}ButtonClicked`,
        buttonType,
        element,
        ...extraInfo,
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(contactCollectorTelemetry);
const useContactCollectorTelemetry = useTelemetry;
export { TelemetryProvider, useContactCollectorTelemetry };
